<template>
  <action-dispatcher
    action="workout/getExercise"
    :parameters="$route.params.id"
    class="view-workout-exercise px-4 px-lg-5 text-light py-5"
    @success="successLoad"
  >
    <router-link
      :to="{ name: 'workout' }"
      class="text-secondary small text-decoration-none"
    >
      <i class="fas fa-chevron-left" /> Seja forte
    </router-link>

    <div v-if="!exercising" class="row mt-4">
      <div class="col-lg-4">
        <div
          class="exercise-image"
          :style="`background-image: url(${exercise.image})`"
        >
          <div class="gradient">
            <h2 class="text-warning font-condensed">{{ exercise.title }}</h2>
            <button
              @click="exercising = true"
              class="btn btn-warning btn-block btn-strong rounded-pill"
            >
              INICIAR
            </button>
          </div>
        </div>
      </div>
      <div class="col-lg-8">
        <h5>{{ seriesLength }} Exercícios</h5>
        <p class="text-secondary mb-4">{{ exercise.subtitle }}</p>

        <div class="card mb-3" v-for="serie in exercise.series" :key="serie.id">
          <div class="card-header p-0">
            <button @click="collapse[serie.id] = !collapse[serie.id]">
              <img :src="serie.image" />
              <div class="flex-fill">
                <strong>{{ serie.title }}</strong>
                <div class="small text-secondary">
                  {{ serie.number_series }} séries x {{ serie.cycles }} rep.
                </div>
              </div>
              <i
                class="fas text-secondary"
                :class="{
                  'fa-chevron-down': !collapse[serie.id],
                  'fa-chevron-up': collapse[serie.id]
                }"
              />
            </button>
          </div>
          <b-collapse v-model="collapse[serie.id]">
            <div class="card-body" v-html="serie.description" />
          </b-collapse>
        </div>
      </div>
    </div>

    <div v-else class="row mt-4">
      <div class="col-lg-4">
        <video-loop :key="currentSerie.id" :stream-url="currentSerie.video" />
      </div>
      <div class="col-lg-4">
        <strong>{{ currentSerie.title }}</strong>
        <div class="small text-secondary mb-4">
          {{ currentSerie.number_series }} séries x
          {{ currentSerie.cycles }} rep.
        </div>
        <div v-html="currentSerie.description" />
      </div>
      <div class="col-lg-4">
        <div class="card">
          <div
            v-for="rep in currentSerie.number_series"
            :key="rep"
            class="card-body my-2 py-2 d-flex align-items-center"
            :class="{ 'bg-warning': reps[currentSerie.id][rep].finished }"
          >
            <b-form-checkbox v-model="reps[currentSerie.id][rep].finished">
              <strong class="ml-3">0{{ rep }}</strong>
            </b-form-checkbox>
            <div class="text-white-transparent text-center flex-fill">
              {{ currentSerie.cycles }} rep.
            </div>
            <form-group
              v-model="reps[currentSerie.id][rep].weight"
              :id="`reps-weight-${rep}`"
              mask="###"
              class="mb-0"
              placeholder="0"
            />
            <div class="text-white-transparent ml-2">kg</div>
          </div>
        </div>
      </div>
      <div class="col-12 text-right pt-5">
        <button
          @click="back"
          class="btn btn-secondary btn-strong rounded-pill mr-3"
        >
          VOLTAR
        </button>
        <button
          @click="next"
          :disabled="disabledNext"
          :class="{
            'btn-warning': !disabledNext,
            'btn-secondary': disabledNext
          }"
          class="btn btn-strong rounded-pill px-5"
        >
          <span v-if="lastSerie">FINALIZAR</span>
          <span v-else>PRÓXIMO</span>
        </button>
      </div>
    </div>

    <b-modal v-model="dialog" hide-header hide-footer centered>
      <confirm-exercise
        :exercise="exercise"
        :reps="reps"
        :loading="loading"
        @confirm="submit"
      />
    </b-modal>
  </action-dispatcher>
</template>

<script>
import { mapState } from "vuex";
import { get } from "lodash";
import VideoLoop from "@/components/workout/VideoLoop";
import ConfirmExercise from "@/components/workout/ConfirmExercise";

export default {
  components: { VideoLoop, ConfirmExercise },
  data() {
    return {
      collapse: {},
      reps: {},
      exercising: false,
      step: 0,
      dialog: false,
      loading: false
    };
  },
  computed: {
    ...mapState("workout", ["exercise"]),
    seriesLength() {
      return get(this.exercise, "series.length");
    },
    currentSerie() {
      return get(this.exercise, `series[${this.step}]`);
    },
    lastSerie() {
      return this.step === this.seriesLength - 1;
    },
    disabledNext() {
      for (let i = 1; i <= this.currentSerie.number_series; i++) {
        if (!this.reps[this.currentSerie.id][i].finished) {
          return true;
        }
      }
      return false;
    }
  },
  methods: {
    successLoad() {
      this.exercise.series.forEach(serie => {
        this.$set(this.reps, serie.id, {});
        for (let i = 1; i <= serie.number_series; i++) {
          this.$set(this.reps[serie.id], i, {
            finished: false,
            weight: ""
          });
        }
      });
    },
    back() {
      if (this.step === 0) {
        this.exercising = false;
      } else {
        this.step--;
      }
    },
    next() {
      if (this.disabledNext) {
        return;
      }
      if (this.lastSerie) {
        this.dialog = true;
      } else {
        this.step++;
      }
    },
    submit() {
      const data = {
        exercise_id: parseInt(this.$route.params.id),
        data: {
          training: {
            id: this.exercise.id,
            title: this.exercise.title,
            subtitle: this.exercise.subtitle,
            image: this.exercise.image
          },
          exercises: this.exercise.series.map(s => ({
            id: s.id,
            title: s.title,
            image: s.image,
            series: Object.values(this.reps[s.id])
              .filter(r => r.finished)
              .map(r => ({
                repetitions: s.cycles,
                weight: parseFloat(r.weight || 0)
              }))
          }))
        }
      };
      this.loading = true;
      this.$store
        .dispatch("workout/saveExercise", data)
        .then(() => {
          this.dialog = false;
          this.$router.push({ name: "workout" });
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.view-workout-exercise {
  .card {
    background: #1c1c1c;
    border-radius: 5px;

    .card-header {
      button {
        background: none;
        border: none;
        color: white;
        padding: 8px 16px 8px 8px;
        display: flex;
        align-items: center;
        text-align: left;
        width: 100%;

        img {
          width: 74px;
          height: 66px;
          object-fit: cover;
          margin-right: 8px;
        }
      }
    }
  }

  .exercise-image {
    background-size: cover;
    background-position: center center;
    padding-top: 245px;

    .gradient {
      text-align: center;
      padding: 80px 28px 32px;
      background: linear-gradient(180deg, rgba(19, 18, 18, 0) 0%, #131212 100%);
    }
  }

  .text-white-transparent {
    color: rgba(255, 255, 255, 0.7);
  }

  ::v-deep .form-control {
    background-color: #131212;
    font-weight: 500;
    width: 64px;
    text-align: center;
  }

  ::v-deep .custom-control-label {
    cursor: pointer;

    &::before {
      border-radius: 100%;
      background-color: transparent;
      border: 1px solid white;
    }

    &::before,
    &::after {
      width: 24px;
      height: 24px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
}
</style>
